import { Link } from "react-router-dom";
import { ArrowRight, Check, Package } from "@untitledui/icons";
import type { FC } from "react";
import { Button } from "@/components/base/buttons/button";

export default function HeroSection() {
  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-brand-200/10 rounded-full filter blur-3xl animate-spin-slow" />
        <div className="absolute bottom-1/3 left-1/4 w-[400px] h-[400px] bg-brand-400/10 rounded-full filter blur-3xl animate-spin-slow" />
      </div>

      <div className="landing-container relative">
        <div className="max-w-4xl mx-auto text-center mb-12 md:mb-16">
          {/* Badge */}
          <div className="inline-block mb-6 animate-fade-in">
            <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-brand-50 text-brand-700">
              <span className="w-2 h-2 mr-2 rounded-full bg-brand-500 animate-pulse-soft" />
              Garage Management Platform
            </span>
          </div>

          {/* Heading */}
          <h1 className="heading-1 text-text-primary mb-6 animate-fade-in">
            Run Your Garage{" "}
            <br className="hidden sm:block" />
            <span className="text-gradient">Smarter</span>
          </h1>

          {/* Subtitle */}
          <p className="subtitle-1 mb-8 max-w-3xl mx-auto animate-fade-in">
            The all-in-one platform for mechanics and shop owners. Manage jobs,
            appointments, parts inventory, storage rentals, and invoicing — all
            from one dashboard.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in">
            <Link to="/login">
              <Button color="primary" size="lg" className="w-full sm:w-auto" iconTrailing={ArrowRight as FC<{ className?: string }>}>
                Get Started Free
              </Button>
            </Link>
            <a href="#how-it-works">
              <Button color="secondary" size="lg" className="w-full sm:w-auto">
                See How It Works
              </Button>
            </a>
          </div>
        </div>

        {/* Hero Visual */}
        <div className="relative max-w-5xl mx-auto mt-12 md:mt-16 animate-fade-in">
          <div className="absolute inset-0 bg-gradient-to-t from-bg-primary to-transparent z-10 h-16 bottom-0 top-auto" />

          <div className="relative glassmorphism rounded-xl card-shadow overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-100/20 to-brand-300/10 z-0" />
            <div className="relative z-10 p-8 md:p-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Active Jobs", value: "24", color: "text-brand-600" },
                  { label: "Today's Appointments", value: "8", color: "text-success-600" },
                  { label: "Parts in Stock", value: "342", color: "text-warning-600" },
                  { label: "Storage Units", value: "18", color: "text-purple-600" },
                ].map((stat) => (
                  <div key={stat.label} className="bg-bg-primary/80 rounded-lg p-4 text-center">
                    <p className={`text-2xl md:text-3xl font-bold ${stat.color}`}>
                      {stat.value}
                    </p>
                    <p className="text-xs md:text-sm text-text-tertiary mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Floating Cards */}
          <div className="absolute -top-6 -right-6 glassmorphism p-4 rounded-lg card-shadow animate-float hidden md:block z-20">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-success-100 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-success-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-text-primary">12 Jobs This Week</p>
                <p className="text-xs text-text-tertiary">+23% vs last week</p>
              </div>
            </div>
          </div>

          <div className="absolute bottom-10 -left-6 glassmorphism p-4 rounded-lg card-shadow animate-float hidden md:block z-20" style={{ animationDelay: "1s" }}>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
                <Package className="w-4 h-4 text-brand-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-text-primary">Parts Inventory</p>
                <p className="text-xs text-text-tertiary">Stock levels tracked</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
